import { useState, useEffect, useRef } from "react";
import { Building2, Users, AlertTriangle, Check, Zap } from "lucide-react";

interface MultiVenueVisualProps {
  isHovering: boolean;
}

// Multi-Venue Visual - Operations dashboard showing staff reallocation across venues
export const MultiVenueVisual = ({ isHovering }: MultiVenueVisualProps) => {
  const [animationPhase, setAnimationPhase] = useState<'idle' | 'scanning' | 'alert' | 'solved'>('idle');
  const [pulseRings, setPulseRings] = useState<number[]>([]);
  const ringIdRef = useRef(0);

  // Animation sequence
  useEffect(() => {
    if (!isHovering) {
      setAnimationPhase('idle');
      setPulseRings([]);
      return;
    }

    // Phase 1: Scanning
    setAnimationPhase('scanning');

    // Phase 2: Alert detected
    const alertTimer = setTimeout(() => {
      setAnimationPhase('alert');
      ringIdRef.current++;
      setPulseRings([ringIdRef.current]);
    }, 1200);

    // Phase 3: Solved
    const solvedTimer = setTimeout(() => {
      setAnimationPhase('solved');
      ringIdRef.current++;
      setPulseRings(prev => [...prev, ringIdRef.current]);
    }, 3500);

    return () => {
      clearTimeout(alertTimer);
      clearTimeout(solvedTimer);
    };
  }, [isHovering]);

  // Clean up old pulse rings
  useEffect(() => {
    if (pulseRings.length === 0) return;
    const timer = setTimeout(() => {
      setPulseRings(prev => prev.slice(1));
    }, 1200);
    return () => clearTimeout(timer);
  }, [pulseRings]);

  const downtownStaff = animationPhase === 'solved' ? 4 : 5;
  const mallStaff = animationPhase === 'solved' ? 3 : 2;
  const isActive = isHovering && animationPhase !== 'idle';
  const isSolved = animationPhase === 'solved';
  const isAlert = animationPhase === 'alert';

  return (
    <div className="relative w-full h-full min-h-[160px] flex items-center justify-center overflow-hidden">
      {/* Main SVG for connections */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 200 140"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Connection: Downtown to Mall (ACTIVE PATH) - left-center to top-right */}
        <path
          d="M 55 70 L 145 35"
          fill="none"
          stroke={isSolved ? '#22c55e' : isAlert ? 'var(--secondary-hex)' : 'rgba(var(--secondary-rgb), 0.2)'}
          strokeWidth={isAlert ? 2 : 1.5}
          className="transition-all duration-700"
        />

        {/* Connection: Downtown to Airport - left-center to bottom-right */}
        <path
          d="M 55 70 L 145 105"
          fill="none"
          stroke="rgba(var(--secondary-rgb), 0.15)"
          strokeWidth="1"
          className={`transition-opacity duration-1000 ${isActive ? 'opacity-100' : 'opacity-40'}`}
        />

        {/* Connection: Mall to Airport - top-right to bottom-right */}
        <path
          d="M 155 45 L 155 95"
          fill="none"
          stroke="rgba(var(--secondary-rgb), 0.15)"
          strokeWidth="1"
          className={`transition-opacity duration-1000 ${isActive ? 'opacity-100' : 'opacity-40'}`}
        />

        {/* Alert pulse rings at Mall (top-right) */}
        {pulseRings.map((id) => (
          <circle
            key={id}
            cx="155"
            cy="35"
            r="6"
            fill="none"
            stroke={isSolved ? '#22c55e' : '#f59e0b'}
            strokeWidth="1.5"
            style={{
              animation: 'pulse-ring 1.5s ease-out forwards',
            }}
          />
        ))}

        {/* Node connection points */}
        <circle cx="50" cy="70" r="2" fill="var(--secondary-hex)" opacity={isActive ? 0.8 : 0.3}/>
        <circle cx="155" cy="105" r="2" fill="var(--secondary-hex)" opacity={isActive ? 0.5 : 0.2}/>
        <circle
          cx="155"
          cy="35"
          r="3"
          fill={isSolved ? '#22c55e' : isAlert ? '#f59e0b' : 'var(--secondary-hex)'}
          opacity={isActive ? 1 : 0.3}
        />
      </svg>

      {/* Venue Cards - Compact */}

      {/* Downtown - Source venue (has extra staff) - middle left */}
      <div
        className={`absolute left-[8%] top-1/2 -translate-y-1/2 transition-all duration-700 ${
          isActive ? 'opacity-100' : 'opacity-70'
        }`}
      >
        <div
          className={`relative w-[76px] rounded-lg border backdrop-blur-sm transition-all duration-700 ${
            isActive && !isSolved
              ? 'bg-card/80 border-secondary/40'
              : 'bg-card/60 border-border/30'
          }`}
        >
          {/* Status indicator */}
          <div className={`absolute -top-1 -right-1 w-2 h-2 rounded-full transition-all duration-500 ${
            isActive && !isSolved ? 'bg-secondary' : 'bg-muted-foreground/30'
          }`}/>

          <div className="p-2">
            {/* Header */}
            <div className="flex items-center gap-1.5 mb-1.5">
              <Building2 className={`h-3 w-3 transition-colors duration-500 ${
                isActive ? 'text-secondary' : 'text-muted-foreground'
              }`} />
              <div className="text-[9px] font-bold tracking-wide text-foreground">DOWNTOWN</div>
            </div>

            {/* Staff count */}
            <div className={`flex items-center justify-between p-1.5 rounded transition-all duration-500 ${
              isActive ? 'bg-secondary/10' : 'bg-muted/20'
            }`}>
              <div className="flex items-center gap-1">
                <Users className={`h-3 w-3 ${isActive ? 'text-secondary' : 'text-muted-foreground'}`} />
                <span className="text-[8px] text-muted-foreground">Staff</span>
              </div>
              <span className={`text-sm font-bold tabular-nums transition-all duration-500 ${
                isActive ? 'text-secondary' : 'text-foreground'
              }`}>
                {downtownStaff}
              </span>
            </div>

            {/* Status text */}
            <div className={`mt-1 text-center text-[7px] font-medium tracking-wide transition-all duration-500 ${
              isActive && !isSolved ? 'text-secondary' : 'text-muted-foreground/50'
            }`}>
              {isActive && !isSolved ? 'SENDING →' : 'OK'}
            </div>
          </div>
        </div>
      </div>

      {/* Mall - Target venue (understaffed) - top-right */}
      <div
        className={`absolute right-[8%] top-[10%] transition-all duration-700 ${
          isActive ? 'opacity-100' : 'opacity-70'
        }`}
      >
        <div
          className={`relative w-[76px] rounded-lg border backdrop-blur-sm transition-all duration-700 ${
            isSolved
              ? 'bg-card/80 border-green-500/50'
              : isAlert
                ? 'bg-card/80 border-amber-500/50'
                : 'bg-card/60 border-border/30'
          }`}
        >
          {/* Status indicator */}
          <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full flex items-center justify-center transition-all duration-500 ${
            isSolved ? 'bg-green-500' : isAlert ? 'bg-amber-500' : 'bg-muted-foreground/30'
          }`}>
            {isSolved ? (
              <Check className="h-2 w-2 text-white" />
            ) : isAlert ? (
              <AlertTriangle className="h-2 w-2 text-white" />
            ) : null}
          </div>

          <div className="p-2">
            {/* Header */}
            <div className="flex items-center gap-1.5 mb-1.5">
              <Building2 className={`h-3 w-3 transition-colors duration-500 ${
                isSolved ? 'text-green-500' : isAlert ? 'text-amber-500' : 'text-muted-foreground'
              }`} />
              <div className="text-[9px] font-bold tracking-wide text-foreground">MALL</div>
            </div>

            {/* Staff count */}
            <div className={`flex items-center justify-between p-1.5 rounded transition-all duration-500 ${
              isSolved ? 'bg-green-500/10' : isAlert ? 'bg-amber-500/10' : 'bg-muted/20'
            }`}>
              <div className="flex items-center gap-1">
                <Users className={`h-3 w-3 transition-colors duration-500 ${
                  isSolved ? 'text-green-500' : isAlert ? 'text-amber-500' : 'text-muted-foreground'
                }`} />
                <span className="text-[8px] text-muted-foreground">Staff</span>
              </div>
              <span className={`text-sm font-bold tabular-nums transition-all duration-700 ${
                isSolved ? 'text-green-500' : isAlert ? 'text-amber-500' : 'text-foreground'
              }`}>
                {mallStaff}
              </span>
            </div>

            {/* Status text */}
            <div className={`mt-1 text-center text-[7px] font-medium tracking-wide transition-all duration-500 ${
              isSolved ? 'text-green-500' : isAlert ? 'text-amber-500' : 'text-muted-foreground/50'
            }`}>
              {isSolved ? '✓ STAFFED' : isAlert ? '⚠ LOW' : 'OK'}
            </div>
          </div>
        </div>
      </div>

      {/* Airport - Neutral venue - bottom-right */}
      <div
        className={`absolute right-[8%] bottom-[10%] transition-all duration-700 ${
          isActive ? 'opacity-100' : 'opacity-70'
        }`}
      >
        <div className="relative w-[76px] rounded-lg bg-card/60 border border-border/30 backdrop-blur-sm">
          {/* Status indicator */}
          <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-muted-foreground/30"/>

          <div className="p-2">
            {/* Header */}
            <div className="flex items-center gap-1.5 mb-1.5">
              <Building2 className="h-3 w-3 text-muted-foreground" />
              <div className="text-[9px] font-bold tracking-wide text-foreground">AIRPORT</div>
            </div>

            {/* Staff count */}
            <div className="flex items-center justify-between p-1.5 rounded bg-muted/20">
              <div className="flex items-center gap-1">
                <Users className="h-3 w-3 text-muted-foreground" />
                <span className="text-[8px] text-muted-foreground">Staff</span>
              </div>
              <span className="text-sm font-bold tabular-nums text-foreground">3</span>
            </div>

            {/* Status text */}
            <div className="mt-1 text-center text-[7px] font-medium tracking-wide text-muted-foreground/50">
              OK
            </div>
          </div>
        </div>
      </div>

      {/* Status Banner - Compact */}
      <div
        className={`absolute top-1 left-1/2 -translate-x-1/2 transition-all duration-700 ${
          isActive ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className={`px-2.5 py-1 rounded-full border backdrop-blur-sm transition-all duration-700 ${
          isSolved
            ? 'bg-green-500/10 border-green-500/30'
            : isAlert
              ? 'bg-amber-500/10 border-amber-500/30'
              : 'bg-secondary/10 border-secondary/30'
        }`}>
          <div className="flex items-center gap-1.5">
            <Zap className={`h-2.5 w-2.5 transition-colors duration-500 ${
              isSolved ? 'text-green-400' : isAlert ? 'text-amber-400' : 'text-secondary'
            }`} />
            <span className={`text-[8px] font-semibold tracking-wide transition-colors duration-500 ${
              isSolved ? 'text-green-400' : isAlert ? 'text-amber-400' : 'text-secondary'
            }`}>
              {isSolved
                ? 'COMPLETE'
                : isAlert
                  ? 'SHORTAGE'
                  : 'SCANNING...'}
            </span>
          </div>
        </div>
      </div>

      {/* Idle state hint */}
      <div className={`absolute inset-0 flex items-center justify-center transition-all duration-700 ${
        !isHovering ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}>
        <div className="text-center">
          <span className="text-[9px] text-muted-foreground/40 font-medium tracking-wide">
            Hover to simulate
          </span>
        </div>
      </div>

      {/* CSS Keyframes */}
      <style>{`
        @keyframes pulse-ring {
          0% { r: 6; opacity: 0.6; }
          100% { r: 20; opacity: 0; }
        }
      `}</style>
    </div>
  );
};
